import { ref, computed, watch } from "vue";
import { defineStore } from "pinia";
import { useRoute } from "vue-router";
import { toast } from "vue3-toastify";
import {
  cartStorageKeyForScope,
  migrateLegacyCartIntoKey,
} from "@/utils/shop-storage-scope";

export const useCartStore = defineStore("cart_product", () => {
  const route = useRoute();
  let cart_products = ref([]);
  let orderQuantity = ref(1);
  let cartOffcanvas = ref(false);

  function persistCart() {
    try {
      const key = cartStorageKeyForScope();
      migrateLegacyCartIntoKey(key);
      localStorage.setItem(key, JSON.stringify(cart_products.value));
    } catch {
      /* ignore quota / privacy mode */
    }
  }

  // add_cart_product
  const addCartProduct = (payload) => {
    const isExist = cart_products.value.some((i) => i.id === payload.id);
    if (payload.quantity === 0) {
      toast.error(`Out of stock ${payload.title}`);
    } else if (!isExist) {
      const newItem = {
        ...payload,
        orderQuantity: 1,
      };
      cart_products.value.push(newItem);
      toast.success(`${payload.title} added to cart`);
    } else {
      cart_products.value.map((item) => {
        if (item.id === payload.id) {
          if (typeof item.orderQuantity !== "undefined") {
            if (item.quantity >= item.orderQuantity + orderQuantity.value) {
              item.orderQuantity =
                orderQuantity.value !== 1
                  ? orderQuantity.value + item.orderQuantity
                  : item.orderQuantity + 1;
              toast.success(
                `${orderQuantity.value} ${item.title} added to cart`
              );
            } else {
              toast.error(`No more quantity available for this product!`);
              orderQuantity.value = 1;
            }
          }
        }
        return { ...item };
      });
    }
    persistCart();
  };

  const increment = () => {
    return (orderQuantity.value = orderQuantity.value + 1);
  };

  const decrement = () => {
    return (orderQuantity.value =
      orderQuantity.value > 1 ? orderQuantity.value - 1 : 1);
  };

  const quantityDecrement = (payload) => {
    cart_products.value.map((item) => {
      if (item.id === payload.id) {
        if (typeof item.orderQuantity !== "undefined") {
          if (item.orderQuantity > 1) {
            item.orderQuantity = item.orderQuantity - 1;
            toast.info(`Decrement Quantity For ${item.title}`);
          }
        }
      }
      return { ...item };
    });
    persistCart();
  };

  const removeCartProduct = (payload) => {
    cart_products.value = cart_products.value.filter((p) => p.id !== payload.id);
    toast.error(`${payload.title} removed from cart`);
    persistCart();
  };

  const initializeCartProducts = () => {
    const key = cartStorageKeyForScope();
    migrateLegacyCartIntoKey(key);
    try {
      const cartData = localStorage.getItem(key);
      if (cartData) {
        const parsed = JSON.parse(cartData);
        cart_products.value = Array.isArray(parsed) ? parsed : [];
      } else {
        cart_products.value = [];
      }
    } catch {
      cart_products.value = [];
    }
  };

  /**
   * Clears cart UI after logout without erasing the last logged-in user's saved cart bucket.
   */
  const clearSessionAfterLogout = () => {
    cart_products.value = [];
    try {
      localStorage.setItem(
        cartStorageKeyForScope(null),
        JSON.stringify([])
      );
      localStorage.removeItem("cart_products");
    } catch {
      /* ignore */
    }
  };

  const clear_cart = (opts = {}) => {
    const skipConfirm = opts && opts.skipConfirm === true;
    if (!skipConfirm) {
      const confirmMsg = window.confirm(
        "Are you sure you want to delete all cart items?"
      );
      if (!confirmMsg) return;
    }
    cart_products.value = [];
    persistCart();
  };

  const initialOrderQuantity = () => {
    return (orderQuantity.value = 1);
  };

  const totalPriceQuantity = computed(() => {
    return cart_products.value.reduce(
      (cartTotal, cartItem) => {
        const { price, orderQuantity } = cartItem;
        if (typeof orderQuantity !== "undefined") {
          const itemTotal = price * orderQuantity;
          cartTotal.quantity += orderQuantity;
          cartTotal.total += itemTotal;
        }
        return cartTotal;
      },
      {
        total: 0,
        quantity: 0,
      }
    );
  });

  const handleCartOffcanvas = () => {
    cartOffcanvas.value = !cartOffcanvas.value;
  };

  watch(
    () => route.path,
    () => {
      orderQuantity.value = 1;
    }
  );

  return {
    addCartProduct,
    cart_products,
    quantityDecrement,
    removeCartProduct,
    clear_cart,
    clearSessionAfterLogout,
    initialOrderQuantity,
    totalPriceQuantity,
    handleCartOffcanvas,
    cartOffcanvas,
    orderQuantity,
    increment,
    decrement,
    initializeCartProducts,
  };
});
